const defaultMeta = {
  currentStatus: '',
  hasCustomerShipment: false,
  currentRmaId: null,
  highlightRmaId: null,
  accessScope: 'rma'
};

const toError = async res => {
  let message = res.statusText || 'Request failed';
  try {
    const payload = await res.json();
    if (payload?.message) {
      message = payload.message;
    } else if (payload?.error) {
      message = payload.error;
    }
  } catch (_) {
    // ignore secondary parse errors
  }
  return new Error(message);
};

export function createAuthenticatedProvider({ apiBase }) {
  const normalizedBase = apiBase.replace(/\/$/, '');
  return {
    mode: 'authenticated',
    async fetchDevice(deviceId) {
      const res = await fetch(`${normalizedBase}/device/${encodeURIComponent(deviceId)}`);
      if (!res.ok) {
        throw await toError(res);
      }
      const device = await res.json();
      let rmas = [];
      if (device?.serialNumber) {
        try {
          const rmaRes = await fetch(
            `${normalizedBase}/rma/${encodeURIComponent(device.serialNumber)}`
          );
          if (rmaRes.ok) {
            const payload = await rmaRes.json();
            if (Array.isArray(payload)) {
              rmas = payload;
            }
          }
        } catch (err) {
          console.warn('Failed to load RMA list for device', err);
        }
      }
      return {
        device,
        rmas,
        meta: { ...defaultMeta }
      };
    },
    async submitTracking() {
      throw new Error('Tracking submission is not available in admin mode.');
    }
  };
}

export function createGuestProvider({ apiBase }) {
  const normalizedBase = apiBase.replace(/\/$/, '');
  return {
    mode: 'guest',
    async fetchDevice(deviceParam) {
      const res = await fetch(
        `${normalizedBase}/public/device/${encodeURIComponent(deviceParam)}/preview`
      );
      if (!res.ok) {
        throw await toError(res);
      }
      const payload = await res.json();
      return {
        device: payload.device || null,
        rmas: [],
        meta: { ...defaultMeta, hasActiveRma: Boolean(payload.hasActiveRma) }
      };
    },
    async submitTracking() {
      throw new Error('Sign in to perform this action.');
    }
  };
}

export function createPublicProvider({ apiBase, token }) {
  const normalizedBase = apiBase.replace(/\/$/, '');
  const normalizedToken = typeof token === 'string' ? token.trim() : '';
  const buildQuery = () => `token=${encodeURIComponent(normalizedToken)}`;

  return {
    mode: 'public',
    token: normalizedToken,
    async fetchDevice(deviceParam) {
      const res = await fetch(
        `${normalizedBase}/public/device/${encodeURIComponent(deviceParam)}?${buildQuery()}`
      );
      if (!res.ok) {
        throw await toError(res);
      }
      const payload = await res.json();
      const device = payload.device || null;
      const rmas = Array.isArray(payload.rmas) ? payload.rmas : [];
      const currentRma = payload.currentRma || null;
      const highlightRmaId = payload.highlightRmaId || currentRma?.rmaId || null;
      const hasCustomerShipment = Boolean(
        currentRma?.customerTracking?.trackingNumber ||
          currentRma?.customerShipment?.trackingNumber
      );
      return {
        device,
        rmas,
        meta: {
          currentStatus: payload.currentStatus || (currentRma?.status ?? ''),
          hasCustomerShipment,
          currentRmaId: currentRma?._id || null,
          highlightRmaId,
          accessScope: payload.accessScope || 'rma'
        }
      };
    },
    async submitTracking(rma, payload) {
      const rmaId = rma?.rmaId || rma?._id;
      if (!rmaId) {
        throw new Error('Missing RMA identifier.');
      }
      const body = {
        trackingNumber: payload.trackingNumber,
        carrier: payload.carrier,
        comment: payload.comment,
        shipmentDate: payload.shipmentDate
      };
      const res = await fetch(
        `${normalizedBase}/public/rma/${encodeURIComponent(rmaId)}/tracking?${buildQuery()}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      if (!res.ok) {
        throw await toError(res);
      }
      const payloadJson = await res.json();
      return payloadJson?.rma;
    }
  };
}
